import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { ToastContainer, toast } from 'react-toastify';
import { Form, Button, Row, Col } from 'react-bootstrap';
import CreatableSelect from 'react-select/creatable';
import Select from 'react-select';
import ThumbnailRearrange from '../components/ThumbnailRearrange';
import DocumentManager from '../components/DocumentManager';
import TubeInput from '../components/TubeInput';
import AuthorInput from '../components/AuthorInput';
import ManualLinkInput from '../components/ManualLinkInput';
import { FaTrash, FaEdit, FaLink, FaFilePdf } from 'react-icons/fa';
import 'react-toastify/dist/ReactToastify.css';
import './AddOrEditProduct.css';
import './AddOrEditCRT.css';

const STORE_API_URL = process.env.REACT_APP_API_URL;

const selectableFields = [
  'tags',
  'brand', 
  'manufacturer',
  'model', 
  'aspect', 
  'viewable_size', 
  'resolutions', 
  'application',
  'chassis', 
  'series',
  'cabinet_material',
  'yoke', 
  'horizontal_scan_range', 
  'vertical_scan_range', 
  'deflection', 
  'heater',
  'degaussing', 
  'mask', 
  'chroma', 
  'osd',
  'inputs', 
  'formats', 
  'country_of_manufacture', 
  'flyback', 
  'comb_type',
  'comb_ic',
  'vertical_output_ic',
  'tint', 
  'focus_type', 
  'yoke_horizontal_inductance', 
  'yoke_vertical_inductance', 
  'yoke_horizontal_resistance', 
  'yoke_vertical_resistance', 
  'hot', 
  'remote', 
  'speakers',
  'launched',
];

// All of these should be stored as arrays of *strings* in `crtData`.
const multiSelectFields = [
  'tags',
  'application',
  'resolutions', 
  'speakers', 
  'launched', 
  'yoke', 
  'chassis', 
  'inputs', 
  'formats', 
  'flyback', 
  'remote', 
  'hot', 
  'chroma',
  'osd',
  'comb_ic',
  'vertical_output_ic',
  'mod_boards'
];

function AddOrEditCRT({ isEdit }) {
  const { id } = useParams();
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const [crtData, setCrtData] = useState({
    // Set default values for the diode fields
    mux_blanking_diode: 'true',  // Default to true
    mux_transistor: 'false',  // Default to false
    mux_transistor_base_diode: 'false',  // Default to false
    chassis_stereo: 'false',  // Default to false
    chassis_r_g_b_diodes: 'false',  // Default to false
    chassis_blanking_diode: 'false',  // Default to false
    chassis_blanking_diode_position_before_ground: 'false',  // Default to false
    chassis_blanking_diode_added: 'false',  // Default to false (don't show "add diode" label by default)
    chassis_r_g_b_diodes_added: 'false',  // Default to false (don't show "add diodes" label by default)
    chassis_blanking_line_enabled: 'false',  // Default to false (only enabled when Direct Injection is true)
    chassis_change_caps_label: 'false',  // Default to false
    mux_rgb_input_capacitors: '0.1',  // Default to 0.1μF for direct injection
    rgb_mux_calculator_enabled: 'false', // Default to false
    tubes: [], // Initialize tubes as empty array
  });
  const [attributes, setAttributes] = useState({});
  const [options, setOptions] = useState({}); // global options for each attribute

  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [crtGallery, setCrtGallery] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [summaryLoading, setSummaryLoading] = useState(false); // Track summary generation loading state
  const [manuals, setManuals] = useState([]);
  const [manualLinks, setManualLinks] = useState([]);
  const [availableModBoards, setAvailableModBoards] = useState([]);
  const [sharedDocuments, setSharedDocuments] = useState([]);
  
  const documentManagerRef = useRef(null);

  const generateSlug = (str) =>
    str
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');

  // Helper function to migrate old tube/tube_brand data to new tubes format
  const migrateTubeData = (crt) => {
    // If we already have tubes data, use it
    if (crt.tubes && Array.isArray(crt.tubes) && crt.tubes.length > 0) {
      return crt.tubes;
    }

    // Otherwise, migrate from old format
    const tubes = [];
    const tubeModels = Array.isArray(crt.tube) ? crt.tube : (crt.tube ? [crt.tube] : []);
    const tubeBrands = Array.isArray(crt.tube_brand) ? crt.tube_brand : (crt.tube_brand ? [crt.tube_brand] : []);

    // Create paired entries, filtering out invalid data
    const maxLength = Math.max(tubeModels.length, tubeBrands.length);
    for (let i = 0; i < maxLength; i++) {
      const model = tubeModels[i] || '';
      const brand = tubeBrands[i] || '';
      
      // Filter out obviously invalid entries
      if (model && 
          model.trim() !== '' && 
          model.toLowerCase() !== 'test' &&
          brand.toLowerCase() !== 'tube') {
        tubes.push({
          model: model.trim(),
          brand: brand.trim()
        });
      }
    }

    return tubes;
  };

  const getProgressColor = (progress) => {
    if (progress < 50) return '#f44336'; // Red
    if (progress < 75) return '#ff9800'; // Orange
    return '#4caf50'; // Green
  };

  useEffect(() => {
    if (isEdit) {
      fetchCRT();
    } else {
      // If we're adding a new CRT, optionally pre-fill the title from ?title=...
      const params = new URLSearchParams(location.search);
      const titleFromQuery = params.get('title');
      if (titleFromQuery) {
        setCrtData(prev => ({ ...prev, title: titleFromQuery }));
      }
    }

    fetchAttributes();
    fetchModBoards();
  }, [isEdit]);

  useEffect(() => {
    if (!isEdit) return;
  
    const timeout = setTimeout(() => {
      autoSaveCRT();
    }, 5000); // Debounce: 5s after last change
  
    return () => clearTimeout(timeout);
  }, [crtData]);

  useEffect(() => {
    if (isEdit) {
      const handleScroll = () => {
        const originalRearrange = document.getElementById('original-rearrange');
        const stickyRearrange = document.getElementById('sticky-rearrange');
        
        if (originalRearrange && stickyRearrange) {
          const rect = originalRearrange.getBoundingClientRect();
          const triggerPoint = 100; // Adjust this value to control when the sticky behavior starts
          
          // Check if we've scrolled past the original rearrange section
          if (rect.top <= triggerPoint) {
            stickyRearrange.style.display = 'block';
            originalRearrange.style.opacity = '0';
            originalRearrange.style.visibility = 'hidden';
          } else {
            stickyRearrange.style.display = 'none';
            originalRearrange.style.opacity = '1';
            originalRearrange.style.visibility = 'visible';
          }
        }
      };
      
      window.addEventListener('scroll', handleScroll);
      
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isEdit, crtGallery]);

  const fetchCRT = async () => {
    try {
      const response = await fetch(`${STORE_API_URL}/crts/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Error fetching CRT data.');
      const crt = await response.json();

      // Extract mod board IDs if they are populated objects
      const modBoardIds = Array.isArray(crt.mod_boards) 
        ? crt.mod_boards.map(board => typeof board === 'string' ? board : board._id)
        : [];

      // Migrate tube data to new format
      const migratedTubes = migrateTubeData(crt);

      // Handle manual_links - ensure it's an array 
      // TODO: this is a hack to get the manual links to work. We can remove this once string based manual links are removed.
      let parsedManualLinks = [];
      if (crt.manual_links) {
        try {
          if (typeof crt.manual_links === 'string') {
            parsedManualLinks = JSON.parse(crt.manual_links);
          } else if (Array.isArray(crt.manual_links)) {
            parsedManualLinks = crt.manual_links;
          }
        } catch (e) {
          console.error('Error parsing manual_links:', e);
          parsedManualLinks = [];
        }
      }

      // Ensure all multiSelectFields are arrays
      const processedData = { ...crt };
      multiSelectFields.forEach(field => {
        if (processedData[field]) {
          processedData[field] = Array.isArray(processedData[field]) 
            ? processedData[field] 
            : [processedData[field]];
        } else {
          processedData[field] = [];
        }
      });

      setCrtData({
        ...processedData,
        tags: Array.isArray(processedData.tags) ? processedData.tags : [],
        mod_boards: modBoardIds,
        tubes: migratedTubes,
        authors: Array.isArray(processedData.authors) ? processedData.authors : [],
      });
      setCrtGallery(crt.gallery_images || []);
      setManuals(crt.manuals || []);
      setManualLinks(Array.isArray(crt.manual_links) ? crt.manual_links : []);
      
      // Load shared documents
      if (crt.shared_documents && Array.isArray(crt.shared_documents)) {
        setSharedDocuments(crt.shared_documents);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const fetchAttributes = async () => {
    try {
      const response = await fetch(`${STORE_API_URL}/crtattributes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch attributes.');

      const data = await response.json();
      setAttributes(data);

      // Convert each attribute's possible values into { label, value } for react-select
      const newOptions = {};
      Object.keys(data).forEach(key => {
        newOptions[key] = data[key]?.values?.map(v => ({ label: v, value: v })) || [];
      });
      setOptions(newOptions);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const fetchModBoards = async () => {
    try {
      const response = await fetch(`${STORE_API_URL}/mod-boards`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch mod boards.');
      const data = await response.json();
      setAvailableModBoards(data);
    } catch (error) {
      console.error('Error fetching mod boards:', error);
      // Don't show toast error for this, as it's not critical
    }
  };

  /**
   * Called by react-select's onChange
   * 
   * For multiSelect fields: receives an array of { label, value } objects
   * For single fields: receives either a single object { label, value } or null
   * 
   * We store just the *string* values in `crtData`.
   */
  const handleChange = (name, selected) => {
    if (multiSelectFields.includes(name)) {
      // It's an array of objects
      // e.g. [{ label: 'ABC', value: 'ABC' }]
      const stringVals = (selected || []).map(obj => obj.value);
      setCrtData(prev => ({ ...prev, [name]: stringVals }));
    } else {
      // It's a single object or null
      const stringVal = selected?.value || '';
      setCrtData(prev => ({ ...prev, [name]: stringVal }));
    }
  };

  /**
   * Called by react-select's onCreateOption when user types a new value
   * We add that to the global 'options' so it shows up in the dropdown.
   * Then we also call `handleChange` passing the new item + existing selection
   */
  const handleCreateOption = async (name, inputValue) => {
    const newOption = { label: inputValue, value: inputValue };

    // Add to global option list so it appears in the dropdown
    setOptions(prev => ({
      ...prev,
      [name]: [...(prev[name] || []), newOption],
    }));

    // Merge with the current selection
    if (multiSelectFields.includes(name)) {
      // read the existing selection from `crtData` (which are strings)
      const currentStrings = Array.isArray(crtData[name]) ? crtData[name] : [];
      // convert them to objects
      const currentObjs = currentStrings.map(s => ({ label: s, value: s }));
      // add the new object
      const merged = [...currentObjs, newOption];
      // pass them to handleChange, which will store them as strings
      handleChange(name, merged);
    } else {
      // It's a single field
      handleChange(name, newOption);
    }

    // For "tags", we skip updating the backend attribute list
    if (name === 'tags') return;

    // For everything else, we update the global attribute list in the DB
    try {
      if (!attributes[name]?.id) throw new Error(`No ID found for attribute "${name}".`);

      const updatedValues = [...(attributes[name]?.values || []), inputValue]
        .filter((v, i, arr) => arr.indexOf(v) === i) // remove duplicates
        .sort((a, b) => String(a).localeCompare(String(b)));

      const response = await fetch(`${STORE_API_URL}/crtattributes/${attributes[name].id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ values: updatedValues }),
      });

      if (!response.ok) throw new Error('Failed to add attribute.');
      toast.success(`Added "${inputValue}" to ${name}`);
      fetchAttributes();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Handle creating new tube models
  const handleCreateTubeModel = async (inputValue) => {
    await handleCreateOption('tube_model', inputValue);
  };

  // Handle creating new tube brands
  const handleCreateTubeBrand = async (inputValue) => {
    await handleCreateOption('tube_brand', inputValue);
  };

  const autoSaveCRT = async (dataOverride = {}, showToast = false) => {
    setSaveStatus('saving');
  
    try {
      const method = isEdit ? 'PUT' : 'POST';
      const url = isEdit ? `${STORE_API_URL}/crts/${id}` : `${STORE_API_URL}/crts`;
      
      const payload = {
        ...crtData,
        gallery_images: crtGallery,
        manuals: manuals,
        manual_links: manualLinks, // Send as array directly
        shared_documents: sharedDocuments,
        tubes: crtData.tubes || [], // Ensure tubes field is included
        ...dataOverride,
      };
  
      if (!payload.tags || payload.tags.length === 0) {
        payload.tags = [payload.model, payload.brand, payload.series].filter(Boolean);
      }
  
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
  
      if (!response.ok) throw new Error('Failed to save CRT data.');
  
      const result = await response.json();

      if (showToast && result.message) toast.success(result.message);
      setSaveStatus('saved');
      
      if (!isEdit && result.crt?._id) {
        navigate(`/superadmin/edit-crt/${result.crt._id}`);
        return;
      }
      
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      setSaveStatus('error');
      toast.error(error.message);
    }
  };  

  const handleRemoveImage = async (idToRemove) => {  
    // Find image from gallery
    const imageToRemove = crtGallery.find(
      img => img.key === idToRemove || `gallery-${crtGallery.indexOf(img)}` === idToRemove
    );
  
    if (!imageToRemove) {
      console.log('No matching image found for ID:', idToRemove);
      return;
    }
  
    try {
      // DELETE from your server (this physically deletes from Spaces + your DB, if you set that up)
      await fetch(`${STORE_API_URL}/upload/crts/${crtData.slug}/images`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          key: imageToRemove.key,
          cropped_key: imageToRemove.cropped_key,
          compressed_key: imageToRemove.compressed_key,
        })
      });
  
      // Then remove it from local state so UI updates
      const updatedGallery = crtGallery.filter(img => img !== imageToRemove);
      setCrtGallery(updatedGallery);
  
      // Persist the new gallery ordering
      autoSaveCRT({ gallery_images: updatedGallery });
  
    } catch (err) {
      toast.error('Failed to delete image from server');
      console.error(err);
    }
  };
  
  /**
   * Generates a summary of the CRT based on available data
   */
  const generateCRTSummary = async () => {
    setSummaryLoading(true);
    try {
      // Show loading toast
      const toastId = toast.info('Generating summary...', { autoClose: false });
      
      // Call the backend API to generate the summary
      const response = await fetch(`${STORE_API_URL}/crts/generate-summary`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(crtData)
      });

      // Dismiss the loading toast
      toast.dismiss(toastId);
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate summary');
      }
      
      const data = await response.json();
      toast.success('Summary generated!');
      return data.summary;
    } catch (error) {
      toast.error(error.message || 'Error generating summary');
      console.error('Error generating summary:', error);
      
      // Fallback to local generation if API fails
      return fallbackGenerateSummary();
    } finally {
      setSummaryLoading(false);
    }
  };
  
  /**
   * Fallback method to generate summary locally if API call fails
   */
  const fallbackGenerateSummary = () => {
    // Extract key fields from crtData
    const { 
      brand, model, viewable_size, aspect, inputs, 
      launched, rgb_modifiable, tube, chassis, 
      application, manufacturer, country_of_manufacture 
    } = crtData;
    
    let parts = [];
    
    // Basic identification
    if (brand && model) {
      parts.push(`${brand} ${model}`);
    }
    
    // Size and aspect ratio
    if (viewable_size) {
      parts.push(`${viewable_size}" display`);
      if (aspect) {
        parts[parts.length - 1] += ` with ${aspect} aspect ratio`;
      }
    }
    
    // Applications/use case
    if (Array.isArray(application) && application.length > 0) {
      parts.push(`designed for ${application.join(', ')}`);
    }
    
    // Manufacturing info
    if (manufacturer && country_of_manufacture) {
      parts.push(`manufactured by ${manufacturer} in ${country_of_manufacture}`);
    } else if (country_of_manufacture) {
      parts.push(`manufactured in ${country_of_manufacture}`);
    }
    
    // Launch year
    if (Array.isArray(launched) && launched.length > 0) {
      parts.push(`released in ${launched.join(', ')}`);
    }
    
    // Technical details
    let techDetails = [];
    if (tube && Array.isArray(tube) && tube.length > 0) {
      techDetails.push(`${tube.join('/')} tube`);
    }
    if (chassis && Array.isArray(chassis) && chassis.length > 0) {
      techDetails.push(`${chassis.join('/')} chassis`);
    }
    if (techDetails.length > 0) {
      parts.push(`featuring ${techDetails.join(' and ')}`);
    }
    
    // Input types
    if (Array.isArray(inputs) && inputs.length > 0) {
      parts.push(`supports ${inputs.join(', ')} inputs`);
    }
    
    // Modding capability
    if (rgb_modifiable === 'Yes') {
      parts.push('RGB modifiable');
    }
    
    // Combine all parts into a coherent summary
    let summary = parts.join(', ');
    
    // Capitalize first letter and add period if not empty
    if (summary) {
      summary = summary.charAt(0).toUpperCase() + summary.slice(1);
      if (!summary.endsWith('.')) {
        summary += '.';
      }
    }
    
    return summary;
  };

  /**
   * Renders a label + CreatableSelect for each attribute in `selectableFields`.
   * We map the *strings* in `crtData[name]` to { label, value } so react-select
   * can display them as tag chips or single selects.
   */
  const renderAttributeInput = (name) => {
    const isMulti = multiSelectFields.includes(name);

    // Convert what's in `crtData[name]` (strings) into react-select objects
    const currentValue = isMulti
      ? (crtData[name] || []).map(val => ({
          label: val,
          value: val
        }))
      : crtData[name]
      ? { label: crtData[name], value: crtData[name] }
      : null;

    return (
      <div className="form-group" key={name}>
        <label>{attributes[name]?.display_name || name}</label>
        <CreatableSelect
          styles={{ container: (provided) => ({ ...provided, width: '100%' }) }}
          name={name}
          isMulti={isMulti}
          options={options[name] || []}
          value={currentValue}
          onChange={(selected) => handleChange(name, selected)}
          onCreateOption={(inputValue) => handleCreateOption(name, inputValue)}
          isClearable
          placeholder={`Select or add ${attributes[name]?.display_name || name}`}
          classNamePrefix="dark-select"
        />
      </div>
    );
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !crtData.slug) return;

    const formData = new FormData();
    files.forEach(file => formData.append('images', file));
    
    // Show progress bar immediately and simulate progress
    setUploadProgress(5);
    
    // Start progress simulation
    const simulateProgress = () => {
      setUploadProgress(current => {
        // Only simulate up to 90% - the final 10% will happen when the response is received
        if (current < 90) {
          return current + Math.floor(Math.random() * 10);
        }
        return current;
      });
    };
    
    // Set up simulation interval
    const progressInterval = setInterval(simulateProgress, 500);

    try {
      const res = await fetch(`${STORE_API_URL}/upload/crts/${crtData.slug}/images`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });

      // Clear interval and complete progress
      clearInterval(progressInterval);
      setUploadProgress(100);

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload failed');

      toast.success('Images uploaded!');
      const newGallery = [...crtGallery, ...data.images];
      setCrtGallery(newGallery);

      // Force a save right after upload
      autoSaveCRT({ gallery_images: newGallery });
      
      // Reset progress after a short delay
      setTimeout(() => {
        setUploadProgress(0);
      }, 1500);
    } catch (err) {
      // Clear interval and reset on error
      clearInterval(progressInterval);
      setUploadProgress(0);
      toast.error(err.message);
    }
  };

  // Update function to delete legacy manuals
  // TODO: Remove this once we've migrated all manuals to the new system
  const handleDeleteManual = async (key) => {
    try {
      console.log('Attempting to delete manual with key:', key);
      console.log('CRT slug:', crtData.slug);
      console.log('API URL:', `${STORE_API_URL}/upload/crts/${crtData.slug}/manuals`);
      
      // DELETE from your server
      const response = await fetch(`${STORE_API_URL}/upload/crts/${crtData.slug}/manuals`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ key })
      });
      
      console.log('Delete response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.text();
        console.error('Delete response error:', errorData);
        throw new Error(`HTTP ${response.status}: ${errorData}`);
      }
      
      const result = await response.json();
      console.log('Delete response success:', result);
  
      // Then remove it from local state so UI updates
      const updatedManuals = manuals.filter(manual => manual.key !== key);
      setManuals(updatedManuals);
  
      // Persist the new manuals list
      autoSaveCRT({ manuals: updatedManuals });
      toast.success('Manual deleted from storage');
  
    } catch (err) {
      toast.error(`Failed to delete manual: ${err.message}`);
      console.error('Delete manual error:', err);
    }
  };

  // Function to update manual type
  const handleManualTypeChange = (key, type) => {
    const updatedManuals = manuals.map(manual => 
      manual.key === key ? { ...manual, type } : manual
    );
    setManuals(updatedManuals);
    autoSaveCRT({ manuals: updatedManuals });
  };

  return (
    <div className="container">
      <ToastContainer />
      <div className="headline d-flex justify-content-between align-items-center">
        <h2 className="underline">{isEdit ? 'Edit CRT' : 'Add CRT'}</h2>
        <div className="save-status text-end">
          {saveStatus === 'saving' && <small className="text-muted">Saving…</small>}
          {saveStatus === 'saved' && <small className="text-success">All changes saved ✓</small>}
          {saveStatus === 'error' && <small className="text-danger">Failed to save ⚠</small>}
        </div>
      </div>

      <div>
        <h6>
          <Link to="/superadmin/crts">CRT Database</Link>
          {' | '}
          {isEdit && crtData.slug ? (
            <>
              <Link to={`/crts/${crtData.slug}`} target="_blank" rel="noopener noreferrer">
                {crtData.title || '(Untitled)'}
              </Link>
              {' | '}
              <Link to={`/rgb-mux-calculator?crt_slug=${crtData.slug}`} target="_blank" rel="noopener noreferrer">
                RGB MUX Calculator
              </Link>
              {crtData.crt_details_link && (
                <>
                  {' | '}
                  <a href={crtData.crt_details_link} target="_blank" rel="noopener noreferrer">
                    CRT Details
                  </a>
                </>
              )}
            </>
          ) : (
            (isEdit ? 'Edit ' + (crtData.title || '(Untitled)') : 'Add CRT')
          )}
        </h6>
      </div>

      <div className="product-form-container mt-3">
        <Row>
          {/* LEFT COLUMN */}
          <Col lg={6} md={12}>
            {/* Title */}
            <div className="form-group">
              <label>Title</label>
              <Form.Control
                type="text"
                value={crtData.title || ''}
                onChange={(e) => {
                  const newTitle = e.target.value;
                  setCrtData((prev) => ({
                    ...prev,
                    title: newTitle,
                    slug: !slugManuallyEdited ? generateSlug(newTitle) : prev.slug
                  }));
                }}
              />
            </div>
            
            {/* Summary */}
            <div className="form-group">
              <label>Summary</label>
              <div style={{width:'100%'}}>
              <Form.Control
                as="textarea"
                rows={3}
                value={crtData.summary || ''}
                onChange={(e) => setCrtData(prev => ({ ...prev, summary: e.target.value }))}
              />
              <div className="mt-2">
                <Button 
                  size="sm" 
                  variant="outline-secondary" 
                  onClick={async () => {
                    const summary = await generateCRTSummary();
                    setCrtData(prev => ({ ...prev, summary }));
                  }}
                  disabled={summaryLoading}
                >
                  {summaryLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Generating...
                    </>
                  ) : (
                    'Generate Summary'
                  )}
                </Button>
              </div>
              </div>
            </div>
            
            <div className="form-group">
              <label>
                Slug
                <br />
                <small className="form-text text-muted">URL-friendly name</small>
              </label>
              <Form.Control
                type="text"
                value={crtData.slug || ''}
                onChange={(e) => {
                  setSlugManuallyEdited(true);
                  setCrtData(prev => ({ ...prev, slug: generateSlug(e.target.value) }));
                }}
              />
            </div>
            {/* Render remaining selectable fields */}
            {selectableFields.map(renderAttributeInput)}
          </Col>

          {/* RIGHT COLUMN */}
          <Col lg={6} md={12}>
            {/* Author Input Section */}
            <div className="form-group">
              <AuthorInput
                authors={crtData.authors || []}
                onChange={(authors) => setCrtData(prev => ({ ...prev, authors }))}
                disabled={false}
                token={token}
              />
            </div>
            <div className="form-group">
              <Form.Control
                type="text"
                value={crtData.url || ''}
                onChange={(e) => setCrtData(prev => ({ ...prev, url: e.target.value }))}
              />
            </div>
            {/* Shared Documents Manager - Replaces old manual upload system */}
            <div className="form-group">
              <DocumentManager
                ref={documentManagerRef}
                selectedDocuments={sharedDocuments}
                onDocumentsChange={(docs) => {
                  setSharedDocuments(docs);
                  // Auto-save when documents change
                  setTimeout(() => autoSaveCRT({ shared_documents: docs }), 500);
                }}
                disabled={false}
                crtId={id}
              />
            </div>

            {/* Legacy Manuals Display (if any exist) */}
            {/* TODO: Remove this once we've migrated all manuals to the new system */}
            {manuals.length > 0 && (
              <div className="form-group">
                <div className="legacy-manuals-list mb-3" style={{width:'100%'}}>
                  {manuals.map((manual, index) => (
                    <div key={index} className="selected-document d-flex align-items-center mb-2 p-2 border rounded" style={{ width: '100%' }}>
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center">
                          <a href={manual.url} target="_blank" rel="noopener noreferrer" className="text-decoration-none me-2">
                            <FaFilePdf className="text-danger" />
                          </a>
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center">
                              <strong 
                                className="text-truncate me-2" 
                                style={{ 
                                  maxWidth: '300px',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}
                                title={manual.file_name || `Manual ${index + 1}`}
                              >
                                {manual.file_name || `Manual ${index + 1}`}
                              </strong>
                              {manual.type && manual.type !== 'unknown' && (
                                <span className="badge bg-secondary me-2">{manual.type}</span>
                              )}
                            </div>
                            <div className="d-flex align-items-center mt-1">
                              {manual.size && (
                                <small className="text-muted me-2">
                                  {(manual.size / (1024 * 1024)).toFixed(2)} MB
                                </small>
                              )}
                              <Form.Select
                                value={manual.type || 'unknown'}
                                onChange={(e) => handleManualTypeChange(manual.key, e.target.value)}
                                size="sm"
                                style={{ width: '140px', fontSize: '0.85em' }}
                              >
                                <option value="unknown">Type...</option>
                                <option value="Service Manual">Service Manual</option>
                                <option value="User Manual">User Manual</option>
                                <option value="Training Document">Training Document</option>
                                <option value="Technical Specs">Technical Specs</option>
                                <option value="Schematics">Schematics</option>
                                <option value="Datasheet">Datasheet</option>
                              </Form.Select>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDeleteManual(manual.key)}
                          className="d-flex align-items-center justify-content-center"
                          title="Delete Manual (removes file from storage)"
                          style={{ height: '32px', width: '32px', padding: 0 }}
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Manual Links */}
            <div className="form-group">
              <ManualLinkInput
                manualLinks={manualLinks}
                onChange={(updatedLinks) => {
                  setManualLinks(updatedLinks);
                  autoSaveCRT({ manual_links: updatedLinks });
                }}
                disabled={false}
              />
            </div>

            {/* Some other text fields */}
            <div className="form-group">
              <Form.Control
                type="text"
                value={crtData.crt_details_link || ''}
                onChange={(e) => setCrtData(prev => ({ ...prev, crt_details_link: e.target.value }))}
                placeholder="URL to general CRT information"
              />
            </div>
            {/* Tubes Section */}
            <h6 className="mt-4 mb-3" style={{ borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>Tube Information</h6>
            <div className="form-group">
              <TubeInput
                tubes={crtData.tubes || []}
                onChange={(tubes) => setCrtData(prev => ({ ...prev, tubes }))}
                disabled={false}
                tubeModelOptions={options.tube_model || []}
                tubeBrandOptions={options.tube_brand || []}
                onCreateTubeModel={handleCreateTubeModel}
                onCreateTubeBrand={handleCreateTubeBrand}
              />
            </div>
            <h6 className="mt-4 mb-3" style={{ borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>Power, Weight & Dimensions</h6>
            <div className="form-group">
              <label>Weight (lbs)</label>
              <Form.Control
                type="text"
                value={crtData.weight || ''}
                onChange={(e) => setCrtData(prev => ({ ...prev, weight: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Power (W)</label>
              <Form.Control
                type="text"
                value={crtData.power || ''}
                onChange={(e) => setCrtData(prev => ({ ...prev, power: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Stereo Input?
                <br />
                <small className="form-text text-muted"></small>
              </label>
              <div className="mt-2 toggle-container" style={{width:'100%'}}>
                <Form.Check 
                  type="switch"
                  id="chassis-stereo-toggle"
                  checked={crtData.chassis_stereo === 'true'}
                  onChange={(e) => setCrtData(prev => ({ 
                    ...prev, 
                    chassis_stereo: e.target.checked ? 'true' : 'false' 
                  }))}
                />
              </div>
            </div>
            <h6 className="mt-4 mb-3" style={{ borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>Mod Information</h6>
            <div className="form-group">
              <label>Compatible Mod Boards</label>
              <Select
                styles={{ container: (provided) => ({ ...provided, width: '100%' }) }}
                name="mod_boards"
                isMulti
                options={availableModBoards.map(board => ({
                  label: `${board.name}${board.category ? ` (${board.category})` : ''}`,
                  value: board._id,
                  board: board // Store the full board object
                }))}
                value={(crtData.mod_boards || []).map(boardId => {
                  // Find the board in availableModBoards to get the display info
                  const foundBoard = availableModBoards.find(board => board._id === boardId);
                  if (foundBoard) {
                    return {
                      label: `${foundBoard.name}${foundBoard.category ? ` (${foundBoard.category})` : ''}`,
                      value: foundBoard._id,
                      board: foundBoard
                    };
                  }
                  return null;
                }).filter(Boolean)}
                onChange={(selected) => {
                  // Store only the board IDs
                  const selectedBoardIds = (selected || []).map(option => option.value);
                  setCrtData(prev => ({ ...prev, mod_boards: selectedBoardIds }));
                }}
                isClearable
                placeholder="Select compatible mod boards"
                classNamePrefix="dark-select"
              />
            </div>
            <div className="form-group">
              <label>Mods</label>
              <Form.Control
                type="text"
                value={crtData.mods || ''}
                onChange={(e) => setCrtData(prev => ({ ...prev, mods: e.target.value }))}
              />
            </div>
            {/* Tri-state fields */}
            {['rgb_modifiable', 'component_modifiable', 'svideo_modifiable', 'composite_modifiable'].map((field) => (
              <div className="form-group" key={field}>
                <label>{field.replace('_', ' ').toUpperCase()}</label>
                <div className="radio-options" style={{marginLeft:'0px', width:'100%'}}>
                  <Form.Check
                    type="radio"
                    label="Yes"
                    name={field}
                    checked={crtData[field] === 'Yes'}
                    onChange={() => setCrtData(prev => ({ ...prev, [field]: 'Yes' }))}
                    inline
                    className="option-check"
                  />
                  <Form.Check
                    type="radio"
                    label="No"
                    name={field}
                    checked={crtData[field] === 'No'}
                    onChange={() => setCrtData(prev => ({ ...prev, [field]: 'No' }))}
                    inline
                    className="option-check"
                  />
                  <Form.Check
                    type="radio"
                    label="Unknown"
                    name={field}
                    checked={crtData[field] === '' || !crtData[field]}
                    onChange={() => setCrtData(prev => ({ ...prev, [field]: '' }))}
                    inline
                    className="option-check"
                  />
                </div>
              </div>
            ))}
            {crtData.component_modifiable === 'Yes' && (
              <>
                <div className="form-group">
                  <label>Component mod link</label>
                  <Form.Control
                    type="text"
                    value={crtData.component_mod_link || ''}
                    onChange={(e) => setCrtData(prev => ({ ...prev, component_mod_link: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Component mod notes</label>
                  <Form.Control
                    as="textarea"
                    style={{ fontFamily: 'monospace', fontSize: '10px' }}
                    rows={12}
                    value={crtData.component_mod_notes || ''}
                    onChange={(e) => setCrtData(prev => ({ ...prev, component_mod_notes: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Composite mod link</label>
                  <Form.Control
                    type="text"
                    value={crtData.composite_mod_link || ''}
                    onChange={(e) => setCrtData(prev => ({ ...prev, composite_mod_link: e.target.value }))}
                  />
                </div>
              </>
            )}
            {crtData.svideo_modifiable === 'Yes' && (
              <>
                <div className="form-group">
                  <label>S-Video mod link</label>
                  <Form.Control
                    type="text"
                    value={crtData.svideo_mod_link || ''}
                    onChange={(e) => setCrtData(prev => ({ ...prev, svideo_mod_link: e.target.value }))}
                  />
                </div>
              </>
            )}
            {(crtData.rgb_modifiable === 'Yes' || crtData.rgb_modifiable === 'No') && (
              <>
                <h6 className="mt-4 mb-3" style={{ borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>RGB mod/not-moddable details</h6>
                <div className="form-group">
                  <label>RGB mod link</label>
                  <Form.Control
                    type="text"
                    value={crtData.rgb_mod_link || ''}
                    onChange={(e) => setCrtData(prev => ({ ...prev, rgb_mod_link: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>RGB mod notes</label>
                  <Form.Control
                    as="textarea"
                    style={{ fontFamily: 'monospace', fontSize: '10px' }}
                    rows={12}
                    value={crtData.rgb_mod_notes || ''}
                    onChange={(e) => setCrtData(prev => ({ ...prev, rgb_mod_notes: e.target.value }))}
                  />
                </div>

                {/* RGB MUX Calculator Section */}
                {crtData.rgb_modifiable === 'Yes' && (
                <div className="form-group">
                  <label>RGB MUX Calculator Settings</label>
                  <div className="mt-2 toggle-container" style={{width:'100%'}}>
                    <Form.Check 
                      type="switch"
                      id="rgb-mux-toggle"
                      checked={crtData.rgb_mux_calculator_enabled === 'true'}
                      onChange={(e) => setCrtData(prev => ({ 
                        ...prev, 
                        rgb_mux_calculator_enabled: e.target.checked ? 'true' : 'false' 
                      }))}
                      className="mb-0"
                    />
                  </div>
                </div>
                )}
              </>
            )}

            {crtData.rgb_modifiable === 'Yes' && crtData.rgb_mux_calculator_enabled === 'true' && (
              <>
              {/* RGB Direct Injection */}
              <div className="form-group">
                <label>RGB Direct Injection?</label>
                <div className="mt-2 toggle-container" style={{width:'100%'}}>
                  <Form.Check 
                    type="switch"
                    id="rgb-direct-injection-toggle"
                    checked={crtData.rgb_direct_injection === 'true'}
                    onChange={(e) => setCrtData(prev => ({ 
                      ...prev, 
                      rgb_direct_injection: e.target.checked ? 'true' : 'false',
                      // When Direct Injection is enabled, enable chassis blanking line; when disabled, disable it
                      chassis_blanking_line_enabled: e.target.checked ? 'true' : 'false'
                    }))}
                    className="mb-0"
                  />
                </div>
              </div>

              {/* Chassis Blanking Line - only show when RGB Direct Injection is enabled */}
              {crtData.rgb_direct_injection === 'true' && (
                <div className="form-group">
                  <label>Chassis Blanking Line Enabled?</label>
                  <div className="mt-2 toggle-container" style={{width:'100%'}}>
                    <Form.Check 
                      type="switch"
                      id="chassis-blanking-line-enabled-toggle"
                      checked={crtData.chassis_blanking_line_enabled === 'true'}
                      onChange={(e) => setCrtData(prev => ({ 
                        ...prev, 
                        chassis_blanking_line_enabled: e.target.checked ? 'true' : 'false' 
                      }))}
                      className="mb-0"
                    />
                  </div>
                </div>
              )}
              <div className="form-group">
                <label>Chassis R, G, B Diodes
                  <br />
                  <small className="form-text text-muted"></small>
                </label>
                <div className="mt-2 toggle-container" style={{width:'100%'}}>
                  <Form.Check 
                    type="switch"
                    id="chassis_r_g_b_diodes-toggle"
                    checked={crtData.chassis_r_g_b_diodes === 'true'}
                    onChange={(e) => setCrtData(prev => ({ 
                      ...prev, 
                      chassis_r_g_b_diodes: e.target.checked ? 'true' : 'false' 
                    }))}
                  />
                </div>
              </div>
              {crtData.chassis_r_g_b_diodes === 'true' && (
              <div className="form-group">
                <label>Add R, G, B Diodes?
                </label>
                <div className="mt-2 toggle-container" style={{width:'100%'}}>
                  <Form.Check 
                    type="switch"
                    id="chassis-r-g-b-diodes-needs-adding-toggle"
                    checked={crtData.chassis_r_g_b_diodes_added === 'true'}
                    onChange={(e) => setCrtData(prev => ({ 
                      ...prev, 
                      chassis_r_g_b_diodes_added: e.target.checked ? 'true' : 'false' 
                    }))}
                    label="Diodes added during mod?"
                  />
                </div>
              </div>
              )}

              <div className="form-group">
                <label>Chassis Blanking Diode
                  <br />
                  <small className="form-text text-muted"></small>
                </label>
                <div className="mt-2 toggle-container" style={{width:'100%'}}>
                  <Form.Check 
                    type="switch"
                    id="chassis_blanking_diode-toggle"
                    checked={crtData.chassis_blanking_diode === 'true'}
                    onChange={(e) => setCrtData(prev => ({ 
                      ...prev, 
                      chassis_blanking_diode: e.target.checked ? 'true' : 'false' 
                    }))}
                  />
                </div>
              </div>

              {/* Chassis Blanking Diode Position - only show when chassis blanking diode is enabled */}
              {crtData.chassis_blanking_diode === 'true' && (
                <div className="form-group">
                  <label>Blanking Diode Before Chassis Ground</label>
                  <div className="mt-2 toggle-container" style={{width:'100%'}}>
                    <Form.Check 
                      type="switch"
                      id="chassis-blanking-diode-position-before-ground-toggle"
                      checked={crtData.chassis_blanking_diode_position_before_ground === 'true'}
                      onChange={(e) => setCrtData(prev => ({ 
                        ...prev, 
                        chassis_blanking_diode_position_before_ground: e.target.checked ? 'true' : 'false' 
                      }))}
                    />
                  </div>
                </div>
              )}
              {crtData.chassis_blanking_diode === 'true' && (
                <div className="form-group">
                  <label>Add Blanking Diode?</label>
                  <div className="mt-2 toggle-container" style={{width:'100%'}}>
                    <Form.Check 
                      type="switch"
                      id="chassis-blanking-diode-needs-adding-toggle"
                      checked={crtData.chassis_blanking_diode_added === 'true'}
                      onChange={(e) => setCrtData(prev => ({ 
                        ...prev, 
                        chassis_blanking_diode_added: e.target.checked ? 'true' : 'false' 
                      }))}
                      label="Diode added during mod?"
                    />
                  </div>
                </div>
              )}

              {/* RGB mod specifications */}
              <div className="form-group">
                <label>Chassis R, G, B, Blanking Inline Jungle PIN Numbers</label>
                <Form.Control
                  type="text"
                  value={crtData.chassis_inline_jungle_pin_numbers || ''}
                  onChange={(e) => setCrtData(prev => ({ ...prev, chassis_inline_jungle_pin_numbers: e.target.value }))}
                  placeholder="10,11,12,13"
                />
              </div>
              <div className="form-group">
                <label>Chassis R, G, B, Blanking Inline OSD PIN Numbers</label>
                <Form.Control
                  type="text"
                  value={crtData.chassis_inline_osd_pin_numbers || ''}
                  onChange={(e) => setCrtData(prev => ({ ...prev, chassis_inline_osd_pin_numbers: e.target.value }))}
                  placeholder="14,15,16,17"
                />
              </div>
              <div className="form-group">
                <label>Chassis R, G, B Capacitor Numbers</label>
                <Form.Control
                  type="text"
                  value={crtData.chassis_capacitor_numbers || ''}
                  onChange={(e) => setCrtData(prev => ({ ...prev,  chassis_capacitor_numbers: e.target.value }))}
                  placeholder="C1, C2, C3"
                />
              </div>
              <div className="form-group">
                <label>Chassis R, G, B Capacitor Value (uF)</label>
                <Form.Control
                  type="text"
                  value={crtData.chassis_capacitor_value || ''}
                  onChange={(e) => setCrtData(prev => ({ ...prev,  chassis_capacitor_value: e.target.value }))}
                  placeholder="0.1"
                />
              </div>
              <div className="form-group">
                <label>Change Inline Caps?</label>
                <div className="mt-2 toggle-container" style={{width:'100%'}}>
                  <Form.Check 
                    type="switch"
                    id="chassis-change-caps-label-toggle"
                    checked={crtData.chassis_change_caps_label === 'true'}
                    onChange={(e) => setCrtData(prev => ({ 
                      ...prev, 
                      chassis_change_caps_label: e.target.checked ? 'true' : 'false' 
                    }))}
                    className="mb-0"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Chassis R, G, B, Blanking Inline Resistor Numbers</label>
                <Form.Control
                  type="text"
                  value={crtData.chassis_inline_resistor_numbers || ''}
                  onChange={(e) => setCrtData(prev => ({ ...prev, chassis_inline_resistor_numbers: e.target.value }))}
                  placeholder="R201, R202, R203, R208"
                />
              </div>
              <div className="form-group">
                <label>Chassis Inline RGB Resistors Values (Ω)
                  <br />
                  <small className="form-text text-muted">
                    {crtData.chassis_inline_resistor_numbers ? 
                      crtData.chassis_inline_resistor_numbers.split(',').slice(0, 3).join(',').trim() : ''}
                  </small>
                </label>
                <Form.Control
                  type="text"
                  value={crtData.chassis_inline_resistors || ''}
                  onChange={(e) => setCrtData(prev => ({ ...prev, chassis_inline_resistors: e.target.value }))}
                  placeholder="1000"
                />
              </div>
              <div className="form-group">
                <label>Chassis Inline Blanking Resistors Value (Ω)
                  <br />
                  <small className="form-text text-muted">
                    {crtData.chassis_inline_resistor_numbers ? 
                      crtData.chassis_inline_resistor_numbers.split(',')[3]?.trim() : ''}
                  </small>
                </label>
                <Form.Control
                  type="text"
                  value={crtData.chassis_inline_blanking_resistor_value || ''}
                  onChange={(e) => setCrtData(prev => ({ ...prev, chassis_inline_blanking_resistor_value: e.target.value }))}
                  placeholder="2500"
                />
              </div>
              <div className="form-group">
                <label>Chassis R, G, B, Blanking Ground Resistor Numbers</label>
                <Form.Control
                  type="text"
                  value={crtData.chassis_ground_resistor_numbers || ''}
                  onChange={(e) => setCrtData(prev => ({ ...prev, chassis_ground_resistor_numbers: e.target.value }))}
                  placeholder="R204, R205, R206, R207"
                />
              </div>
              <div className="form-group">
                <label>Chassis RGB Ground Resistors (Ω)
                  <br />
                  <small className="form-text text-muted">
                    {crtData.chassis_ground_resistor_numbers ? 
                      crtData.chassis_ground_resistor_numbers.split(',').slice(0, 3).join(',').trim() : ''}
                  </small>
                </label>
                <Form.Control
                  type="text"
                  value={crtData.chassis_ground_resistors || ''}
                  onChange={(e) => setCrtData(prev => ({ ...prev, chassis_ground_resistors: e.target.value }))}
                  placeholder="1000"
                />
              </div>
              <div className="form-group">
                <label>Chassis Blanking Ground Resistors Value (Ω)
                  <br />
                  <small className="form-text text-muted">
                    {crtData.chassis_ground_resistor_numbers ? 
                      crtData.chassis_ground_resistor_numbers.split(',')[3]?.trim() : ''}
                  </small>
                </label>
                <Form.Control
                  type="text"
                  value={crtData.chassis_blanking_ground_resistor_value || ''}
                  onChange={(e) => setCrtData(prev => ({ ...prev, chassis_blanking_ground_resistor_value: e.target.value }))}
                  placeholder="2500"
                />
              </div>
              <div className="form-group">
                <label>Chassis Blanking Inductor
                  <br />
                  <small className="form-text text-muted"></small>
                </label>
                <div className="mt-2 toggle-container" style={{width:'100%'}}>
                  <Form.Check 
                    type="switch"
                    id="chassis-blanking-inductor-toggle"
                    checked={crtData.chassis_blanking_inductor === 'true'}
                    onChange={(e) => setCrtData(prev => ({ 
                      ...prev, 
                      chassis_blanking_inductor: e.target.checked ? 'true' : 'false' 
                    }))}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Chassis Blanking Inductor Number</label>
                <Form.Control
                  type="text"
                  value={crtData.chassis_blanking_inductor_number || ''}
                  onChange={(e) => setCrtData(prev => ({ ...prev, chassis_blanking_inductor_number: e.target.value }))}
                  placeholder="LN003"
                />
              </div>
              <div className="form-group">
                <label>Analog Chroma Vp-p</label>
                <Form.Select
                  value={crtData.analog_chroma_vpp || ''}
                  onChange={(e) => setCrtData(prev => ({ ...prev, analog_chroma_vpp: e.target.value }))}
                >
                  <option value="">Select voltage</option>
                  <option value="0.5">0.5V</option>
                  <option value="0.7">0.7V</option>
                  <option value="1.0">1.0V</option>
                </Form.Select>
              </div>

              {/* RGB mux board configuration */}
              <h6 className="mt-4 mb-3" style={{ borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>RGB mux board configuration</h6>
              
              <div className="form-group">
                <label>MUX Termination Resistors (Ω)
                  <br />
                  <small className="form-text text-muted">R1, R2, R3</small>
                </label>
                <Form.Control
                  type="text"
                  value={crtData.mux_termination_resistors || ''}
                  onChange={(e) => setCrtData(prev => ({ ...prev, mux_termination_resistors: e.target.value }))}
                  placeholder="75"
                />
              </div>
              <div className="form-group">
                <label>
                  {crtData.rgb_direct_injection === 'true' 
                    ? 'MUX RGB Input Capacitors (μF)' 
                    : 'MUX RGB Input Resistors (Ω)'
                  }
                  <br />
                  <small className="form-text text-muted">R4, R5, R6</small>
                </label>
                <Form.Control
                  type="text"
                  value={crtData.rgb_direct_injection === 'true' 
                    ? (crtData.mux_rgb_input_capacitors || '') 
                    : (crtData.mux_rgb_input_resistors || '')
                  }
                  onChange={(e) => setCrtData(prev => ({ 
                    ...prev, 
                    [crtData.rgb_direct_injection === 'true' 
                      ? 'mux_rgb_input_capacitors' 
                      : 'mux_rgb_input_resistors'
                    ]: e.target.value 
                  }))}
                  placeholder={crtData.rgb_direct_injection === 'true' ? '0.1' : '1000'}
                />
              </div>
              <div className="form-group">
                <label>MUX Audio Resistors (Ω)
                  <br />
                  <small className="form-text text-muted">R7, R8</small>
                </label>
                <Form.Control
                  type="text"
                  value={crtData.mux_audio_resistors || ''}
                  onChange={(e) => setCrtData(prev => ({ ...prev, mux_audio_resistors: e.target.value }))}
                  placeholder="1000"
                />
              </div>
              <div className="form-group">
                <label>MUX Blanking Diode
                  <br />
                  <small className="form-text text-muted">R9 or D9</small>
                </label>
                <div className="mt-2 toggle-container" style={{width:'100%'}}>
                  <Form.Check 
                    type="switch"
                    id="mux_blanking_diode-toggle"
                    checked={crtData.mux_blanking_diode === 'true'}
                    onChange={(e) => setCrtData(prev => ({ 
                      ...prev, 
                      mux_blanking_diode: e.target.checked ? 'true' : 'false' 
                    }))}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>MUX Blanking Ground Resistor
                  <br />
                  <small className="form-text text-muted">R10</small>
                </label>
                <Form.Control
                  type="text"
                  value={crtData.mux_blanking_ground_resistor || ''}
                  onChange={(e) => setCrtData(prev => ({ ...prev, mux_blanking_ground_resistor: e.target.value }))}
                  placeholder="R10"
                />
              </div>
              <div className="form-group">
                <label>MUX Blanking Inline Resistor
                  <br />
                  <small className="form-text text-muted">R11</small> 
                </label>
                <Form.Control
                  type="text"
                  value={crtData.mux_blanking_inline_resistor || ''}
                  onChange={(e) => setCrtData(prev => ({ ...prev, mux_blanking_inline_resistor: e.target.value }))}
                  placeholder="R11"
                />
              </div>
              <div className="form-group">
                <label>MUX Transistor (Q1)
                  <br />
                  <small className="form-text text-muted">Q1</small>
                </label>
                <div className="mt-2 toggle-container" style={{width:'100%'}}>
                  <Form.Check 
                    type="switch"
                    id="mux_transistor"
                    checked={crtData.mux_transistor === 'true'}
                    onChange={(e) => setCrtData(prev => ({ 
                      ...prev, 
                      mux_transistor: e.target.checked ? 'true' : 'false' 
                    }))}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>MUX Transistor Base Resistor
                  <br />
                  <small className="form-text text-muted">R12</small> 
                </label>
                <Form.Control
                  type="text"
                  value={crtData.mux_transistor_base_resistor || ''}
                  onChange={(e) => setCrtData(prev => ({ ...prev, mux_transistor_base_resistor: e.target.value }))}
                  placeholder="R12"
                />
              </div>
              </>
            )}

            {/* CRT Gallery Images */}
            <h6 className="mt-4 mb-3" style={{ borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>CRT Gallery Images</h6>

            {uploadProgress > 0 && (
              <div className="progress mb-3">
                <div
                  className="progress-bar"
                  role="progressbar"
                  style={{ 
                    width: `${uploadProgress}%`,
                    background: getProgressColor(uploadProgress), // dynamic styling for the bar
                    transition: 'width 0.4s ease-in-out', // Smooth width transitions
                    color: '#fff', // White text
                    fontWeight: 'bold', // Bold text for readability
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  aria-valuenow={uploadProgress}
                  aria-valuemin="0"
                  aria-valuemax="100"
                >
                  {uploadProgress}%
                </div>
              </div>
            )}
            <div className="form-group upload-section">
              <div 
                className={`drag-drop-container`}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.add('dragging');
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.remove('dragging');
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.currentTarget.classList.remove('dragging');
                  
                  if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                    // Call the same handler as the file input
                    const event = { target: { files: e.dataTransfer.files } };
                    handleFileUpload(event);
                  }
                }}
              >
                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="upload-icon">
                  <path d="M12 17L12 3" stroke="#9966CC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M7 7L12 3L17 7" stroke="#9966CC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M20 21H4" stroke="#9966CC" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div className="drag-drop-text">Drop images here</div>
                <div className="drag-drop-subtext">or click to browse files</div>
                <div className="drag-drop-subtext">Upload up to 30 images (PNG, JPG, WEBP)</div>
                <input
                  type="file"
                  multiple
                  accept=".jpg,.jpeg,.png,.webp"
                  onChange={handleFileUpload}
                />
              </div>
            </div>
              
            {/* Original Rearrange Section */}
            <div id="original-rearrange" className="rearrange-sticky-container original-rearrange">
              <h5>Re-arrange Images</h5>
              <p>Note: First image in the below set will be the hero image. <b>Click and drag to rearrange!</b></p>
              <ThumbnailRearrange
                imagePreviews={crtGallery.map((img, index) => ({
                  id: img.key || `gallery-${index}`,
                  url: img.compressed_url || img.url
                }))}
                onReorder={(newOrder) => {
                  const reordered = newOrder.map(item =>
                    crtGallery.find(img =>
                      img.key === item.id || `gallery-${crtGallery.indexOf(img)}` === item.id
                    )
                  ).filter(Boolean);
                
                  setCrtGallery(reordered);
                  autoSaveCRT({ gallery_images: reordered }); 
                }}
                onRemove={handleRemoveImage}
              />
            </div>
            
            {/* Sticky Rearrange Section */}
            <div id="sticky-rearrange" className="rearrange-sticky-container sticky-rearrange">
              <h5>Re-arrange Images</h5>
              <p>Note: First image in the below set will be the hero image. <b>Click and drag to rearrange!</b></p>
              <ThumbnailRearrange
                imagePreviews={crtGallery.map((img, index) => ({
                  id: img.key || `gallery-${index}`,
                  url: img.compressed_url || img.url
                }))}
                onReorder={(newOrder) => {
                  const reordered = newOrder.map(item =>
                    crtGallery.find(img =>
                      img.key === item.id || `gallery-${crtGallery.indexOf(img)}` === item.id
                    )
                  ).filter(Boolean);
                
                  setCrtGallery(reordered);
                  autoSaveCRT({ gallery_images: reordered }); 
                }}
                onRemove={handleRemoveImage}
              />
            </div>

            {/* Submit/Cancel */}
            <div className="form-group mt-4">
              <Button
                variant="primary"
                onClick={() => autoSaveCRT({}, true)}
                disabled={saveStatus === 'saving'}
              >
                {saveStatus === 'saving' ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Saving...
                  </>
                ) : (
                  isEdit ? 'Update CRT' : 'Add CRT'
                )}
              </Button>
              <Button
                variant="secondary"
                onClick={() => navigate('/superadmin/crts')}
                className="ms-2"
              >
                Cancel
              </Button>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default AddOrEditCRT;
